import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFormatting } from '../context/FormattingContext';
import { Transaction } from '../types/Transaction';
import { Summary, TransactionFilters, TransactionListMode } from './useTransactions';
import { useAccounts } from './useAccounts';
import { buildQueryParams } from '../utils/buildQueryParams';
import { withForcedLedgerReadMode } from '../utils/ledgerReadMode';
import { authFetch } from '../lib/authFetch';

type LedgerKind =
  | 'income'
  | 'expense'
  | 'transfer'
  | 'cardPurchase'
  | 'cardPayment'
  | 'adjustment';

interface LedgerTransactionDto {
  _id: string;
  amount: number;
  amountSigned?: number; // ✅ some backends send this (esp. adjustments)
  displayAmountSigned?: number;
  date: string;
  description?: string;
  category: string;
  tags?: string[];
  tentative?: boolean;
  ownerScope?: 'primary' | 'partner' | 'child' | 'household';
  visibility?: 'shared' | 'private';
  kind?: LedgerKind;

  accountId?: string;
  fromAccountId?: string;
  toAccountId?: string;

  accountName?: string;
  fromAccountName?: string;
  toAccountName?: string;
  direction?: {
    fromAccountId?: string;
    fromAccountName?: string;
    toAccountId?: string;
    toAccountName?: string;
  };

  accountType?: string;
  fromAccountType?: string;
  toAccountType?: string;
  isRecurring?: boolean;
  seriesId?: string;
  recurringRole?: 'instance' | 'template';
  templateId?: string;
  frequency?: string;
  nextDueDate?: string;
  endDate?: string;
  source?: string;
}

type AccountMeta = { name?: string; accountType?: string };
type AccountMetaMap = Map<string, AccountMeta>;

const normalizeTentativeSummary = (summaryData: any): NonNullable<Summary['tentative']> => ({
  income: summaryData?.tentative?.income ?? 0,
  expense: summaryData?.tentative?.expense ?? 0,
  savings: {
    cumulative: summaryData?.tentative?.savings?.cumulative ?? 0,
  },
  netBalance: summaryData?.tentative?.netBalance ?? 0,
  cashFlowBalance: summaryData?.tentative?.cashFlowBalance ?? 0,
});

const includesName = (value: string | undefined, needle: string): boolean =>
  typeof value === 'string' && value.toLowerCase().includes(needle.toLowerCase());

const isChecking = (accountType?: string, accountName?: string): boolean => {
  if (accountType === 'checking') return true;
  // fallback: name contains "checking"
  return includesName(accountName, 'checking');
};

const mapLedgerType = (kind?: LedgerKind): Transaction['type'] => {
  if (kind === 'income') return 'income';
  if (kind === 'expense' || kind === 'cardPurchase') return 'expense';
  // UI uses "savings" bucket for transfers/card payments
  if (kind === 'transfer' || kind === 'cardPayment') return 'savings';
  // default safe
  return 'expense';
};

const resolveAccountMeta = (
  explicitType: string | undefined,
  explicitName: string | undefined,
  accountId: string | undefined,
  metaMap: AccountMetaMap
): AccountMeta => {
  const fallback = accountId ? metaMap.get(accountId) : undefined;
  return {
    accountType: explicitType ?? fallback?.accountType,
    name: explicitName ?? fallback?.name,
  };
};

/**
 * amountSigned convention here:
 * - represent CASH impact (checking) so table sign feels right
 * - income: +, expense: -, cardPurchase: -, transfer:
 *   - from checking => negative
 *   - to checking   => positive
 */
const mapAmountSigned = (
  dto: LedgerTransactionDto,
  absAmount: number,
  metaMap: AccountMetaMap
): number | undefined => {
  if (typeof dto.displayAmountSigned === 'number') return dto.displayAmountSigned;

  const kind = dto.kind;

  if (kind === 'income') return absAmount;
  if (kind === 'expense' || kind === 'cardPurchase') return -absAmount;

  if (kind === 'adjustment') {
    // if backend gives amountSigned, trust it
    if (typeof dto.amountSigned === 'number') return dto.amountSigned;
    // otherwise best-effort: treat positive amount as increase
    return absAmount;
  }

  if (kind === 'transfer') {
    const fromMeta = resolveAccountMeta(dto.fromAccountType, dto.fromAccountName, dto.fromAccountId, metaMap);
    const toMeta = resolveAccountMeta(dto.toAccountType, dto.toAccountName, dto.toAccountId, metaMap);

    const fromIsCash = isChecking(fromMeta.accountType, fromMeta.name);
    const toIsCash = isChecking(toMeta.accountType, toMeta.name);

    // ✅ cash moving OUT
    if (fromIsCash) return -absAmount;
    // ✅ cash moving IN
    if (toIsCash) return absAmount;

    // fallback (no checking involved): treat as outflow from "from"
    return -absAmount;
  }

  if (kind === 'cardPayment') {
    // typically checking -> credit card (cash outflow)
    const fromMeta = resolveAccountMeta(dto.fromAccountType, dto.fromAccountName, dto.fromAccountId, metaMap);
    if (isChecking(fromMeta.accountType, fromMeta.name)) return -absAmount;
    return -absAmount;
  }

  return undefined;
};

export const useLedgerTransactions = (
  refreshKey: number,
  filters: TransactionFilters = {},
  showFuture = false,
  listMode: TransactionListMode = 'committed'
) => {
  const { auth } = useAuth();
  const formatting = useFormatting();
  const { accounts } = useAccounts();

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [summary, setSummary] = useState<Summary>({
    income: 0,
    expense: 0,
    savings: { cumulative: 0, inRange: 0 },
    priorBalance: 0,
    cashFlowBalance: 0,
    netBalance: 0,
  });
  const [loading, setLoading] = useState(true);

  // staleness guard
  const reqCounterRef = useRef(0);

  // ✅ account meta lookup by id (fallback when ledger DTO doesn’t include types/names)
  const accountMetaById = useMemo<AccountMetaMap>(() => {
    const map = new Map<string, AccountMeta>();
    for (const a of accounts) {
      map.set(a._id, { name: a.name, accountType: a.accountType });
    }
    return map;
  }, [accounts]);

  // stable dep instead of JSON.stringify(filters) inline
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    let cancelled = false;
    const myReqId = ++reqCounterRef.current;

    const fetchAll = async () => {
      setLoading(true);
      try {
        const { type, category, startDate, endDate, isRecurring, tags } = filters;

        const txTentativeParam =
          listMode === 'all' ? undefined : listMode === 'tentative' ? true : false;

        // ✅ DO NOT send legacy "type" filter to ledger endpoint.
        // we map+filter it locally (because ledger doesn't have type, it has kind)
        const ledgerTxQuery = buildQueryParams({
          category,
          startDate,
          endDate,
          isRecurring,
          tentative: txTentativeParam,
          showFuture,
          tags,
        }, {
          clampEndDateToTodayWhenShowFutureFalse: true,
          timeZone: formatting.timeZone,
          omitFalseForKeys: ['showFuture'],
        });

        // summary endpoint still accepts legacy-style filters (and is ledger-backed on server now)
        const summaryQuery = buildQueryParams({
          type,
          category,
          startDate,
          endDate,
          showFuture,
          isRecurring,
          tags,
        }, {
          clampEndDateToTodayWhenShowFutureFalse: true,
          timeZone: formatting.timeZone,
          omitKeys: ['showFuture'],
        });

        const [txRes, summaryRes] = await Promise.all([
          authFetch(`/api/ledger/transactions?${ledgerTxQuery}`, {
            headers: { Authorization: `Bearer ${auth.token}` },
          }),
          authFetch(withForcedLedgerReadMode(`/api/transactions/summary?${summaryQuery}`), {
            headers: { Authorization: `Bearer ${auth.token}` },
          }),
        ]);

        if (cancelled || myReqId !== reqCounterRef.current) return;

        const [txData, summaryData] = await Promise.all([
          txRes.json(),
          summaryRes.json(),
        ]);

        if (cancelled || myReqId !== reqCounterRef.current) return;

        setSummary({
          income: summaryData.income || 0,
          expense: summaryData.expense || 0,
          savings: {
            cumulative: summaryData.savings?.cumulative ?? 0,
            inRange: summaryData.savings?.inRange ?? 0,
          },
          priorBalance: summaryData.priorBalance ?? 0,
          cashFlowBalance: summaryData.cashFlowBalance || 0,
          netBalance: summaryData.netBalance || 0,
          tentative: normalizeTentativeSummary(summaryData),
        });

        const rawLedgerTxs: LedgerTransactionDto[] = Array.isArray(txData)
          ? txData
          : txData?.transactions || [];

        const mapped: Transaction[] = rawLedgerTxs.map((dto) => {
          const absAmount = Math.abs(Number(dto.amount) || 0);
          const kind = dto.kind;

          return {
            _id: dto._id,
            date: dto.date,
            description: dto.description || '',
            category: dto.category,
            tags: dto.tags || [],
            tentative: dto.tentative,
            ownerScope: dto.ownerScope,
            visibility: dto.visibility,

            source: (dto.source || 'ledger') as Transaction['source'],
            kind,
            isRecurring: dto.isRecurring,
            seriesId: dto.seriesId ?? undefined,
            recurringRole: dto.recurringRole ?? undefined,
            templateId: dto.templateId ?? undefined,
            frequency: dto.frequency as Transaction['frequency'],
            nextDueDate: dto.nextDueDate,
            endDate: dto.endDate,

            type: mapLedgerType(kind),
            amount: absAmount,
            amountSigned: mapAmountSigned(dto, absAmount, accountMetaById),
            displayAmountSigned:
              typeof dto.displayAmountSigned === 'number'
                ? dto.displayAmountSigned
                : mapAmountSigned(dto, absAmount, accountMetaById),

            accountId: dto.accountId,
            fromAccountId: dto.fromAccountId,
            toAccountId: dto.toAccountId,
            accountName: dto.accountName,
            fromAccountName: dto.fromAccountName,
            toAccountName: dto.toAccountName,
            direction: dto.direction,
          };
        });

        // ✅ Local filter for legacy "type" UI (income/expense/savings)
        const filteredMapped = type ? mapped.filter((tx) => tx.type === type) : mapped;

        setTransactions(filteredMapped);
      } catch (err) {
        console.error('❌ useLedgerTransactions error:', err);
      } finally {
        if (!cancelled && myReqId === reqCounterRef.current) setLoading(false);
      }
    };

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [auth.token, refreshKey, filtersKey, formatting.timeZone, showFuture, listMode, accountMetaById]);

  return { transactions, summary, loading };
};
